import React, { useEffect } from "react";

import {
  IgAutocomplete,
  IgCheckbox,
  IgChip,
  IgTextField,
} from "@intelligems/ig-theme";
import { CountryEnum, ShopifyCountryList } from "@intelligems/ig-types";
import { Checkbox, Grid, MenuItem } from "@mui/material";

type CountryProps = Omit<React.ComponentProps<typeof Grid>, "onChange"> & {
  value: string[];
  disabled?: boolean;
  onChange?: (value: string[]) => void;
  placeholder?: string;
};

export const Country = ({
  value,
  disabled,
  onChange,
  placeholder,
  ...props
}: CountryProps) => {
  const handleChange = (_ev: React.SyntheticEvent, change: string[]) => {
    onChange?.(change);
  };

  return (
    <Grid item container xs={12} rowSpacing={2} {...props}>
      <IgAutocomplete
        multiple
        disableCloseOnSelect
        value={value}
        disabled={disabled}
        fullWidth={true}
        sx={{ minWidth: "360px" }}
        options={ShopifyCountryList}
        onChange={handleChange}
        getOptionLabel={(option) => `${CountryEnum[option]} (${option})`}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <IgCheckbox checked={selected} sx={{ marginRight: 1 }} />
            {`${CountryEnum[option]} (${option})`}
          </li>
        )}
        renderInput={(params) => {
          return (
            <IgTextField
              {...params}
              disabled={disabled}
              placeholder={placeholder ?? "Countries"}
            />
          );
        }}
        renderTags={() => null}
        clearIcon={null}
      />
      {value.length > 0 && (
        <Grid item container rowSpacing={1}>
          {value.map((countryCode) => (
            <Grid item key={countryCode}>
              <IgChip
                key={countryCode}
                label={`${CountryEnum[countryCode]} (${countryCode})`}
                onDelete={() => {
                  const newValue = value.filter((v) => v !== countryCode);
                  onChange?.(newValue);
                }}
                size={"small"}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Grid>
  );
};

interface CountrySelectProps {
  country: string;
  onChange: (value: any, changeKey: "key" | "filter" | "value") => void;
}

export const CountrySelect = ({ country, onChange }: CountrySelectProps) => {
  // handle when type switched and value not compatible
  useEffect(() => {
    if (!CountryEnum[country]) onChange("", "value");
  }, []);

  return (
    <Grid item>
      <IgAutocomplete
        disablePortal
        sx={{ ...(!country.length && { minWidth: "360px" }) }}
        ListboxProps={{
          style: { maxHeight: "200px" },
        }}
        renderOption={(props, option) => (
          <MenuItem
            onClick={() => {
              onChange(option, "value");
            }}
            value={option}
            key={option}
            sx={{ fontSize: "14px" }}
          >
            {`${CountryEnum[option]} (${option})`}
          </MenuItem>
        )}
        options={ShopifyCountryList}
        getOptionLabel={(option) => `${CountryEnum[option]} (${option})`}
        renderInput={(params) =>
          !country.length ? (
            <IgTextField
              {...params}
              size={"small"}
              variant={"outlined"}
              placeholder={"Choose Country"}
            />
          ) : (
            <IgChip
              key={country}
              label={`${CountryEnum[country]} (${country})`}
              onDelete={() => {
                onChange("", "value");
              }}
            />
          )
        }
      />
    </Grid>
  );
};
